import React, { useState, useRef, useImperativeHandle } from 'react';
import { useMutation } from '@apollo/client'; 
import { Spin, message } from 'antd';
import CREATE_COURSE_CERTIFICATE from '../../gql/mutations/CREATE_COURSE_CERTIFICATE';
import DELETE_COURSE_CERTIFICATE from '../../gql/mutations/DELETE_COURSE_CERTIFICATE';
import FormCertificates from '../FormCertificates/FormCertificates'; 
import FormSaveCancelToolbar from '../FormSaveCancelToolbar/FormSaveCancelToolbar'; 

const TabCertificates = ( { form, isSavingCallback, ref } ) => {
    const formCertificatesRef = useRef( useRef );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ isModified, setIsModified ] = useState( false );
    const [ openConfirm, setOpenConfirm ] = useState( false );
    const [ createCourseCertificate ] = useMutation( CREATE_COURSE_CERTIFICATE );
    const [ deleteCourseCertificate ] = useMutation( DELETE_COURSE_CERTIFICATE );
    
    useImperativeHandle( ref, () => ( {
        isModified() {
            return isModified;
        },
        showSaveConfirm() {
            setOpenConfirm( true );
        }
    } ) );

    const handleSave = async () => {
        setIsSaving( true );
        setOpenConfirm( false ); 
        isSavingCallback( true );
        let hasError = false;
        let needsUpdate = false;

        const oldIds = form.getFieldValue( 'certificateIds' );
        const newIds = form.getFieldValue( 'certificateIdsModified' );
        const addIds = newIds.filter( i => !oldIds.includes( i ) );
        const deleteIds = oldIds.filter( i => !newIds.includes( i ) );
        
        // Delete Certificates
        if ( deleteIds && deleteIds.length > 0 ) {
            const oldCertificates = form.getFieldValue( 'certTemplates' );
            
            try {
                await Promise.all( 
                    deleteIds.map( async i => {
                        const response = await deleteCourseCertificate( {
                            variables: {
                                input: {
                                    id: oldCertificates.find( c => c.certificateTemplate.id === i )?.id
                                }
                            }
                        } );

                        if ( response?.data?.result?.affectedRows ) {
                            needsUpdate = true;

                            let templates = form.getFieldValue( 'certTemplates' );
                            if ( templates && templates !== null ) {
                                const result = templates.filter( t => t.certificateTemplate.id !== i );
                                form.setFieldsValue( { certTemplates: result } );
                            }    

                        }
                    } )
                ); 

                needsUpdate = true;
            } catch ( e ) {
                console.error( 'Error saving certificte', e );
                hasError = true;
            }
        }
        
        // Create Certificates
        if ( addIds && addIds.length > 0 ) {
            try {
                await Promise.all( 
                    addIds.map( async i => {
                        const response = await createCourseCertificate( {
                            variables: {
                                input: {
                                    certificateTemplateId: i,
                                    courseId: form.getFieldValue( 'id' ),
                                    brandId: formCertificatesRef.current.getBrandId( i ),
                                }
                            }
                        } );

                        if ( response?.data?.result?.id ) {
                            needsUpdate = true;

                            // Update existing values
                            const newCert = {
                                id: response?.data?.result?.id,
                                courseId: form.getFieldValue( 'id' ),
                                certificateTemplate: {
                                    id: i
                                }
                            };

                            let templates = form.getFieldValue( 'certTemplates' );
                            if ( !templates || templates === null ) {
                                templates = [];
                            }
                            templates.push( newCert );
                            form.setFieldsValue( { certTemplates: templates } );
                        } else {
                            console.error( 'Error creating certificate', response );
                            hasError = true;
                        }
                    } )
                );
            } catch ( e ) {
                console.error( 'Error saving certificte', e );
                hasError = true;
            }
        }
        
        if ( needsUpdate ) { 
            let templates = form.getFieldValue( 'certTemplates' );
            form.setFieldsValue( { certificateIds: templates.map( i => i.certificateTemplate?.id ) } ); 
            form.setFieldsValue( { certificateIdsModified: [] } ); 
            form.setFieldsValue( { certificateIdsAreModified: false } ); 
        }

        if ( hasError ) {
            message.error( 'Error saving certificates' );
        } else {
            message.success( 'Course saved.' );
        }
        
        setIsSaving( false );
        isSavingCallback( false );
        handleModifiedChange( false );
        formCertificatesRef.current.reset();
    };

    const handleReset = () => {
        formCertificatesRef.current.reset();
        setOpenConfirm( false );
        isSavingCallback( false );
    };

    const handleModifiedChange = ( value ) => {
        setIsModified( value );
    };

    return (
        <div className="resources-tab tab-main-columns">
            <div className="left">
                <Spin spinning={ isSaving }>
                    <FormCertificates 
                        form={ form }
                        ref={ formCertificatesRef }
                        modifiedCallback={ handleModifiedChange } />
                </Spin>
            </div>

            <div className="right">
                <FormSaveCancelToolbar
                    form={ form } 
                    open={ openConfirm }
                    saving={ isSaving }
                    modified={ isModified }
                    saveCallback={ handleSave }
                    cancelCallback={ handleReset }
                />
            </div>
        </div>
    );
};

export default TabCertificates;
